import { apiErrorMessage } from '@agos/api-client'
import { usePostNewsletterSubscribe } from '@agos/api-client/marketing'
import { T, tt } from '@agos/shared'
import { useState } from 'react'

import type { Newsletter } from './module-content'

/**
 * The newsletter box, which collects an address - FRONTEND.md §9.564, FB-853.
 *
 * **It was disabled, citing a `BLOCKED.md` reason that is wrong.** That comment read *"The subscribe call is a send
 * path and is not wired here"*; `ContentService.subscribe` validates an address, checks a name length and inserts a
 * row with `ON CONFLICT DO NOTHING`. **No mailer, no external call.** `BLOCKED.md` grouped it on 2026-08-03 with
 * `sms/send-manual`, the campaign runs and the translation sweeps - *"sends, spends on the AI key, or both"* - and
 * this one is neither, so it was blocked by association with its neighbours in a sentence.
 *
 * **Measured: `newsletter` is enabled in `homepage_modules_content` on both franchises**, so a customer reached
 * this section and found a dead box on something the restaurant had switched on.
 *
 * <p><b>The old comment's second half was right and is kept.</b> It said the field is *"disabled rather than absent
 * so it does not look like a form that silently does nothing"* - which is FB-814's defect avoided deliberately here
 * while the marketing site shipped it. The shape was right; only the premise was false.
 *
 * <p><b>Its own component because it needs state</b>, and a hook cannot live inside the `switch` that renders the
 * modules.
 */
export function NewsletterForm({ newsletter }: { newsletter: Newsletter }) {
  const [email, setEmail] = useState('')
  const [done, setDone] = useState(false)
  const [problem, setProblem] = useState('')
  const subscribe = usePostNewsletterSubscribe()

  const submit = () => {
    const address = email.trim()
    if (address === '') return
    setProblem('')
    subscribe.mutate(
      { data: { email: address } },
      {
        /* **Nothing clears the box here, and a mutation is what said so.** The first draft called `setEmail('')`
           "so a second press cannot send the same address"; removing it left every case green, because on success
           the form is **replaced** by the line below and the input is unmounted - so the clearing was a dead
           line. The replacement is the mechanism, and it is what the case asserts. §9.478's survivor, again. */
        onSuccess: () => setDone(true),
        /* **The server's own sentence, not a generic one.** `subscribe` distinguishes an invalid address from a
           name that is too long, and §10A.58 records the cost of conflating them: a visitor whose *name* was too
           long used to be told their address was wrong. And the box keeps its value, because the address is what
           they have to correct - a form that empties itself when it rejects you is one you retype. */
        onError: (caught) =>
          setProblem(apiErrorMessage(caught, tt('Prihlásenie sa nepodarilo. Skúste to prosím znova.'))),
      },
    )
  }

  if (done) {
    return (
      <p className="text-sm font-semibold text-green-700" data-newsletter-done>
        {/* **It does not promise mail**, because nothing sends any - §9.521's precedent, where *Úhrada
            zaznamenaná* was true and incomplete and both halves had to be said. */}
        <T>Vašu adresu sme si zapísali. Nič vám zatiaľ neposielame.</T>
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <input
          data-newsletter-email
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={newsletter.placeholder}
          className="flex-1 px-4 py-2.5 rounded-xl bg-background border border-border text-sm outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          data-newsletter-submit
          disabled={subscribe.isPending || email.trim() === ''}
          className="px-5 py-2.5 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-60 whitespace-nowrap"
        >
          {newsletter.ctaText}
        </button>
      </form>
      {problem !== '' && (
        <p className="text-xs text-destructive" data-newsletter-error>
          {problem}
        </p>
      )}
    </div>
  )
}
